import { ResponseHeaders, retryAfterSeconds } from "../../lib/request";
import { LastSecurityChangeState, SecurityChangeKind, SessionEndReason } from "../state/session";

// Every refresh reply is classified here and nowhere else. The rule is inverted on purpose:
// only 400, 401 and 404 are terminal, so the next proxy shape or gateway status is transient
// without anyone having to enumerate it.
//
// 401 and 404 are the server saying the session is gone. 400 is different: the request carries an
// empty body, and the only backend that rejects it is one that predates the refresh cookie and
// still wants the token in the body. No retry can succeed against it, so treating it as transient
// would leave the tab on the reconnecting notice for ever and hide the misdeploy behind it.
export enum RefreshOutcome {
  Success = "success",
  Terminal = "terminal",
  Transient = "transient",
}

// `baseRequest` reports a network error or a timeout as `statusCode: null`, and a non-JSON body
// as `type: 'NONE'` with a null payload -- neither is a shape the success branch may destructure.
export interface RefreshResultLike {
  statusCode?: number | null;
  payload?: unknown;
  headers?: ResponseHeaders | null;
}

export const classifyRefreshResult = (
  result: RefreshResultLike | null | undefined,
): RefreshOutcome => {
  const statusCode = result?.statusCode ?? null;

  if (statusCode === 400 || statusCode === 401 || statusCode === 404)
    return RefreshOutcome.Terminal;

  const isSuccessStatus = typeof statusCode === "number" && statusCode >= 200 && statusCode < 300;
  if (!isSuccessStatus) return RefreshOutcome.Transient;

  // a 2xx from a captive portal or a maintenance page carries no access token; treating it as a
  // success would throw on the destructure and kill the coordinator
  const payload = result?.payload as { accessToken?: unknown } | null | undefined;
  const accessToken = payload?.accessToken;
  if (typeof accessToken !== "string" || accessToken.length === 0) return RefreshOutcome.Transient;

  return RefreshOutcome.Success;
};

// Unless the reply says how long to wait: 1s, 5s, 15s, then every 30s with +/-5s jitter for as
// long as the tab stays open.
export const REFRESH_RETRY_STEP_MS: readonly number[] = [1000, 5000, 15000];
export const REFRESH_RETRY_STEADY_MS = 30000;
export const REFRESH_RETRY_JITTER_MS = 5000;

// A server wait is untrusted input on a timer: 0 would retry in a hot loop, and a huge value would
// leave the tab behind the reconnecting notice until the user acts. The ceiling sits well above the
// longest wait the backend sends (its 300s cap plus up to 30s of its own jitter), so that jitter
// reaches the timer intact instead of piling every tab onto one value.
export const REFRESH_RETRY_AFTER_MIN_MS = 1000;
export const REFRESH_RETRY_AFTER_MAX_MS = 600000;

export const serverRetryAfterMs = (result: RefreshResultLike | null | undefined): number | null => {
  const seconds = retryAfterSeconds(result?.headers);
  return seconds === null ? null : seconds * 1000;
};

export const refreshRetryDelayMs = (
  attempt: number,
  retryAfterMs: number | null = null,
  random: () => number = Math.random,
): number => {
  // the server's answer beats the guess. No client jitter goes on top: the backend already jitters
  // every value it sends, and more here could only bring a tab back sooner than it was told.
  if (retryAfterMs !== null && !Number.isNaN(retryAfterMs)) {
    return Math.min(Math.max(retryAfterMs, REFRESH_RETRY_AFTER_MIN_MS), REFRESH_RETRY_AFTER_MAX_MS);
  }

  if (attempt <= REFRESH_RETRY_STEP_MS.length) {
    return REFRESH_RETRY_STEP_MS[Math.max(attempt, 1) - 1];
  }

  // uniform over the closed band, so a fleet of tabs reconnecting after an outage spreads out
  const jitter = Math.round((random() * 2 - 1) * REFRESH_RETRY_JITTER_MS);
  return REFRESH_RETRY_STEADY_MS + jitter;
};

// the refresh 401 body carries no reason, so a specific message is only honest while this
// tab's own security save is recent.
export const SECURITY_CHANGE_REASON_WINDOW_MS = 600000;

export const pickSessionEndReason = (
  lastSecurityChange: LastSecurityChangeState | null | undefined,
  now: number,
): SessionEndReason => {
  if (!lastSecurityChange) return SessionEndReason.SessionEnded;
  if (now - lastSecurityChange.at >= SECURITY_CHANGE_REASON_WINDOW_MS)
    return SessionEndReason.SessionEnded;

  switch (lastSecurityChange.kind) {
    case SecurityChangeKind.Email:
      return SessionEndReason.SecurityChangeEmail;
    case SecurityChangeKind.Mfa:
      return SessionEndReason.SecurityChangeMfa;
    default:
      return SessionEndReason.SessionEnded;
  }
};
